import {
    Button,
    Dialog,
    DialogContent,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { useFormat } from 'Services/localize/format'
import AppDialogTitle from 'components/Dialog/AppDialogTitle'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { useTranslation } from 'react-i18next'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { convertSvgImageToPng } from 'utilities/svgConverter'
import { FetchingStatus } from 'hooks/useFetchApi/useFetchApi'
import PrintReceiptPreviewModal from 'components/PrintReceiptPreview/PrintReceiptPreviewModal'
import { ReceiptTemplateMarkupSlimModel } from 'models'
import { useReceipt } from 'hooks/useReceipt/useReceipt'
import { EMAIL_REGEXP } from 'utilities/stringHelper'
import { PreviewImg, receiptButton } from '../styles/receiptDialog'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import useSendPaymentToCFD from 'hooks/useSendMessageToCFD/useSendPaymentToCFD'
import CloseDialogButton from 'components/Dialog/CloseDialogButton'
import { emailRegex } from 'utilities/regex.utillities'

interface IReceiptDialogProps {
    /** if TRUE will disabled most of button. Default: FALSE */
    loading?: boolean

    sellReceiptTemplate?: ReceiptTemplateMarkupSlimModel
    isSaleComplete?: boolean

    sale?: ReceiptSaleModel
    totalDuleValue?: number
    changeValue: number
    openReceipt: boolean
    onClose: (isLogout: boolean) => void

    paymentTypeID?: number
    showLogoutButton?: boolean
}

export interface ReceiptSaleModel {
    saleID: string
    customerEmail?: string | null
}

export function ReceiptDialog({
    loading = false,
    sellReceiptTemplate,
    sale,
    totalDuleValue,
    changeValue = 0,
    isSaleComplete = false,
    openReceipt,
    paymentTypeID,
    onClose,
    showLogoutButton,
}: IReceiptDialogProps) {
    const { t } = useTranslation()
    const { currency } = useFormat()

    const {
        onPrintReceipt,
        printerStatus,
        isPrinting,
        previewSvg,
        sendReportToEmail,
        parseReportTemplate,
        sendingStatus,
        enableOnScreenPrinter,
        isReceiptReady,
    } = useReceipt({
        saleID: isSaleComplete ? sale?.saleID : '',
        defaultSellReceiptTemplate: sellReceiptTemplate,
    })
    const imgRef = useRef<HTMLImageElement>(null)
    const [email, setEmail] = useState('')
    const [sendingEmail, setSendingEmail] = useState(false)
    const [openPreview, setOpenPreview] = useState(false)

    const handleSendReport = useCallback(
        async (e: any) => {
            e.preventDefault()
            if (!email) return
            setSendingEmail(true)
            parseReportTemplate()

            setTimeout(async () => {
                if (imgRef.current) {
                    const png = convertSvgImageToPng(imgRef.current)
                    if (png) await sendReportToEmail(email, png)
                }
            }, 1000)
        },
        [email, sendReportToEmail, parseReportTemplate],
    )

    const handlePrint = useCallback(async () => {
        if (enableOnScreenPrinter) {
            parseReportTemplate()
            setOpenPreview(true)
        } else {
            await onPrintReceipt('ReceiptDialog')
        }
    }, [onPrintReceipt, parseReportTemplate, enableOnScreenPrinter])

    const resultDisplaySection = () => {
        if (paymentTypeID && paymentTypeID === PAYMENT_TYPE_ID.CREDITCARD)
            return (
                <Typography variant="h3" style={{ fontWeight: 600 }}>
                    {t('quick_sell.receipt_dialog.label.total_charge')}:{' '}
                    {currency(totalDuleValue ?? 0)}
                </Typography>
            )
        return (
            <Typography variant="h3" style={{ fontWeight: 600 }}>
                {t('quick_sell.receipt_dialog.label.change')}:{' '}
                {currency(changeValue)}
            </Typography>
        )
    }
    const { sendCompletePayment } = useSendPaymentToCFD()
    const onCompletePayment = useCallback(() => {
        setEmail(sale?.customerEmail || '')
        sendCompletePayment(
            paymentTypeID === PAYMENT_TYPE_ID.CASH ? changeValue : undefined,
            sale?.customerEmail || '',
        )
    }, [changeValue, paymentTypeID, sale?.customerEmail, sendCompletePayment])

    useEffect(() => {
        if (openReceipt && sale) {
            onCompletePayment()
        }
    }, [onCompletePayment, openReceipt, sale])

    useEffect(() => {
        setSendingEmail(sendingStatus === FetchingStatus.LOADING)
    }, [sendingStatus])

    const onChangeEmail = useCallback(
        (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const val = e.target.value
            setEmail(val)
            if (emailRegex.test(val))
                sendCompletePayment(
                    paymentTypeID === PAYMENT_TYPE_ID.CASH
                        ? changeValue
                        : undefined,
                    val || '',
                )
        },
        [changeValue, paymentTypeID, sendCompletePayment],
    )
    return (
        <Dialog
            open={openReceipt}
            fullWidth
            maxWidth="md"
            onClose={() => onClose(false)}
        >
            <AppDialogTitle text={t('receipt_dialog.label.payment')} />
            <CloseDialogButton
                dialogName="receipt dialog"
                onClick={() => onClose(false)}
            />
            <DialogContent style={{ padding: 5 }}>
                <form onSubmit={handleSendReport}>
                    <Grid
                        container
                        direction="column"
                        sx={{ flexGrow: 1, padding: 1 }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Grid item pb={2}>
                            <CheckCircle
                                style={{ fontSize: 70 }}
                                color="primary"
                            />
                        </Grid>

                        <Grid item pb={2}>
                            <Typography variant="h3">
                                {t(
                                    'quick_sell.receipt_dialog.label.payment_complete',
                                )}
                            </Typography>
                        </Grid>

                        <Grid item pb={2}>
                            {resultDisplaySection()}
                        </Grid>

                        <Grid
                            item
                            container
                            justifyContent="center"
                            spacing={1}
                            pb={1}
                        >
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    placeholder="customer@email.com"
                                    value={email}
                                    disabled={
                                        !isSaleComplete ||
                                        isPrinting ||
                                        loading ||
                                        sendingEmail ||
                                        !isReceiptReady
                                    }
                                    onChange={onChangeEmail}
                                    size="medium"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    size="large"
                                    color="primary"
                                    disabled={
                                        !isSaleComplete ||
                                        !EMAIL_REGEXP.test(email) ||
                                        isPrinting ||
                                        loading ||
                                        sendingEmail ||
                                        !isReceiptReady
                                    }
                                    fullWidth
                                    sx={receiptButton}
                                    type="submit"
                                >
                                    {t(
                                        'quick_sell.receipt_dialog.label.email_receipt',
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            container
                            justifyContent={'space-between'}
                            spacing={1}
                        >
                            <Grid
                                item
                                xs={12}
                                sm={12}
                                md="auto"
                                lg="auto"
                                xl="auto"
                            >
                                <Button
                                    disabled={
                                        !isSaleComplete ||
                                        !printerStatus ||
                                        loading ||
                                        isPrinting ||
                                        sendingEmail ||
                                        !isReceiptReady
                                    }
                                    onClick={handlePrint}
                                    sx={receiptButton}
                                    color="primary"
                                    startIcon={<ReceiptLongIcon />}
                                    size="large"
                                    fullWidth
                                >
                                    {t(
                                        'quick_sell.receipt_dialog.label.print_receipt',
                                    )}
                                </Button>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={12}
                                md="auto"
                                lg="auto"
                                xl="auto"
                            >
                                <Stack direction={'row'} spacing={1}>
                                    {showLogoutButton && (
                                        <Button
                                            onClick={() => onClose(true)}
                                            sx={receiptButton}
                                            variant="outlined"
                                            size="large"
                                            fullWidth
                                        >
                                            {t('receipt_dialog.label.logout')}
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => onClose(false)}
                                        sx={receiptButton}
                                        color="primary"
                                        size="large"
                                        fullWidth
                                    >
                                        {!isSaleComplete || showLogoutButton
                                            ? t('receipt_dialog.label.continue')
                                            : t(
                                                  'quick_sell.receipt_dialog.label.done',
                                              )}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
                <PreviewImg
                    src={previewSvg}
                    alt="receipt-preview"
                    ref={imgRef}
                />
            </DialogContent>
            <PrintReceiptPreviewModal
                title={t('receipt_dialog.label.preview_receipt')}
                open={openPreview}
                onClose={() => setOpenPreview(false)}
                previewMarkup={previewSvg}
            />
        </Dialog>
    )
}
